import React from 'react'
import { PRINT_EVENT } from '../../hooks/usePrinting'
import { useScrollSpy } from '../../hooks/useScrollSpy'
import type { FfpDashboardData, PeriodKey } from '../../types'
import { PERIODS, formatDate, hasReadyComments, longMonth } from '../../utils'
import { SectionLink } from '../SectionLink'
import { track } from '@utils/tracking'
import styles from './header.module.sass'

// The sticky bar's links, in page order.
const SECTIONS = [
  { id: 'health', label: 'Health' },
  { id: 'progress', label: 'Progress' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'feedback', label: 'Ratings and comments' },
]

const SECTION_IDS = SECTIONS.map(({ id }) => id)

// The sticky bar's height plus room: a section is current once its top part
// reaches this far down the screen.
const SPY_OFFSET = 96

type Props = {
  data: FfpDashboardData
  period: PeriodKey
  onPeriod: (period: PeriodKey) => void
}

const orgLine = ({ people, first_month }: FfpDashboardData): string => {
  const parts = [`${people.enrolled} enrolled`, `${people.onboarded} onboarded`]
  if (first_month) parts.push(`programme started ${longMonth(first_month)}`)
  return parts.join(' · ')
}

// Export prints the page. Charts nobody has scrolled to yet mount on the print
// event; two frames let React mount them and Chart.js draw them first.
const onExport = () => {
  track('FFP Dashboard: Export page')
  window.dispatchEvent(new Event(PRINT_EVENT))
  requestAnimationFrame(() => requestAnimationFrame(() => window.print()))
}

export const Header = ({ data, period, onPeriod }: Props) => (
  <>
    <header className={styles.header}>
      <div className={styles.identity}>
        <p className={styles.eyebrow}>wealthbit · Financial Freedom Programme</p>
        <h1 className={styles.name}>{data.organization.name}</h1>
        <p className={styles.line}>{orgLine(data)}</p>
      </div>
      <div className={styles.meta}>
        <Updated at={data.freshness.captured_at} />
        <button type="button" className={styles.export} onClick={onExport}>
          Export
        </button>
      </div>
    </header>
    <StickyBar data={data} period={period} onPeriod={onPeriod} />
  </>
)

const Updated = ({ at }: { at: string | null }) => {
  const date = formatDate(at)
  if (!date) return null

  return <p className={styles.updated}>Updated {date}</p>
}

const StickyBar = ({ data, period, onPeriod }: Props) => {
  const current = useScrollSpy(SECTION_IDS, SPY_OFFSET)
  // Feedback's link says comments are there once any are ready.
  const linkLabel = (id: string, label: string): string => (id === 'feedback' && hasReadyComments(data.feedback) ? 'What your people say' : label)
  // "Since start" would be the 12-month series mislabelled when the first capture is older than it.
  const periods = PERIODS.filter((option) => data.history_complete || option.key !== 'since_start')

  const links = SECTIONS.map(({ id, label }) => (
    <SectionLink key={id} id={id} current={current === id} className={current === id ? `${styles.link} ${styles.current}` : styles.link}>
      {linkLabel(id, label)}
    </SectionLink>
  ))
  const pills = periods.map(({ key, label }) => <PeriodPill key={key} option={key} label={label} selected={key === period} onPeriod={onPeriod} />)

  return (
    <nav className={styles.bar} aria-label="Dashboard">
      <div className={styles.links}>{links}</div>
      <div className={styles.periods} role="group" aria-label="Period">
        {pills}
      </div>
    </nav>
  )
}

type PillProps = {
  option: PeriodKey
  label: string
  selected: boolean
  onPeriod: (period: PeriodKey) => void
}

const PeriodPill = ({ option, label, selected, onPeriod }: PillProps) => (
  <button type="button" aria-pressed={selected} className={selected ? `${styles.period} ${styles.selected}` : styles.period} onClick={() => onPeriod(option)}>
    {label}
  </button>
)
